import React, { useCallback, useEffect, useMemo, useState } from "react";
import DocentesForm from "./DocentesForm";
import docentesService from "../../../../services/docentesService";
import { listarCursos } from "../../../../services/cursosService";
import mallaService from "../../../../services/mallaService";
import { showToast } from "../../../../utils/showToast";

// Editable desde aquí Y desde Malla Curricular a propósito (misma tabla
// cursos_materias_docentes de fondo, sin riesgo de desincronizarse). Ya no
// hay "preferente" protegido — todo docente asociado se puede desmarcar.
function MateriasQueDicta({ docenteId }) {
  const [resumen, setResumen] = useState(null);
  const [resumenError, setResumenError] = useState("");

  const [cursos, setCursos] = useState([]);
  const [cursoId, setCursoId] = useState("");
  const [mallaCurso, setMallaCurso] = useState(null);
  const [mallaLoading, setMallaLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const cargarResumen = useCallback(() => {
    if (!docenteId) return;
    docentesService.materiasQueDicta(docenteId)
      .then((res) => setResumen(res.materias || []))
      .catch((err) => setResumenError(err.message || "No se pudo cargar."));
  }, [docenteId]);

  useEffect(() => {
    setResumen(null);
    setResumenError("");
    cargarResumen();
  }, [cargarResumen]);

  useEffect(() => {
    listarCursos()
      .then((res) => setCursos(Array.isArray(res) ? res : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!cursoId) { setMallaCurso(null); return; }
    setMallaLoading(true);
    mallaService.obtenerMallaPorCurso(cursoId)
      .then((res) => setMallaCurso(Array.isArray(res?.materias) ? res.materias : (Array.isArray(res) ? res : [])))
      .catch((err) => showToast({ type: "danger", message: err.message || "No se pudo cargar la malla de ese curso." }))
      .finally(() => setMallaLoading(false));
  }, [cursoId]);

  const toggleMateria = async (fila, marcado) => {
    setTogglingId(fila.id);
    try {
      if (marcado) {
        await mallaService.agregarDocenteCalificado(fila.id, docenteId);
      } else {
        await mallaService.quitarDocenteCalificado(fila.id, docenteId);
      }
      setMallaCurso((prev) =>
        prev.map((m) =>
          m.id === fila.id
            ? {
                ...m,
                // Si era el preferente historico y se desmarca, el backend
                // tambien limpia cursos_materias.docente_id — reflejamos eso
                // aca para que el checkbox no vuelva a verse marcado.
                docente_id: !marcado && String(m.docente_id) === String(docenteId) ? null : m.docente_id,
                docentes_calificados: marcado
                  ? [...(m.docentes_calificados || []), { id: docenteId }]
                  : (m.docentes_calificados || []).filter((d) => String(d.id) !== String(docenteId)),
              }
            : m
        )
      );
      cargarResumen();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo actualizar." });
    } finally {
      setTogglingId(null);
    }
  };

  const porCurso = useMemo(() => {
    return (resumen || []).reduce((acc, m) => {
      (acc[m.curso_nombre] ||= []).push(m);
      return acc;
    }, {});
  }, [resumen]);
  const cursosConMaterias = Object.keys(porCurso);

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <p className="mb-3 fw-semibold text-uppercase text-muted" style={{ fontSize: "0.8rem", letterSpacing: "0.03em" }}>
          Materias que dicta
        </p>

        {resumenError ? (
          <p className="text-muted small mb-3">{resumenError}</p>
        ) : resumen === null ? (
          <p className="text-muted small mb-3">Cargando...</p>
        ) : cursosConMaterias.length === 0 ? (
          <p className="text-muted small mb-3">No tiene materias asignadas en ningún curso todavía.</p>
        ) : (
          cursosConMaterias.map((curso) => (
            <div key={curso} className="mb-2">
              <div className="small fw-semibold">{curso}</div>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {porCurso[curso].map((m) => (
                  <span key={m.materia_id} className="badge bg-light text-dark border">
                    {m.materia_nombre}
                  </span>
                ))}
              </div>
            </div>
          ))
        )}

        <hr className="my-3" />

        <label className="form-label small fw-semibold">Agregar o quitar por curso</label>
        <select
          className="form-select form-select-sm mb-3"
          value={cursoId}
          onChange={(e) => setCursoId(e.target.value)}
        >
          <option value="">Selecciona un curso...</option>
          {cursos.map((c) => (
            <option key={c.id} value={c.id}>{c.nombre}</option>
          ))}
        </select>

        {mallaLoading && <p className="text-muted small mb-0">Cargando materias del curso...</p>}

        {!mallaLoading && mallaCurso && (
          mallaCurso.length === 0 ? (
            <p className="text-muted small mb-0">Este curso no tiene materias en su malla todavía.</p>
          ) : (
            <div className="d-flex flex-column gap-1" style={{ maxHeight: 220, overflowY: "auto" }}>
              {mallaCurso.map((fila) => {
                const marcado =
                  String(fila.docente_id) === String(docenteId) ||
                  (fila.docentes_calificados || []).some((d) => String(d.id) === String(docenteId));
                return (
                  <div className="form-check" key={fila.id}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`dm-${fila.id}`}
                      checked={marcado}
                      disabled={togglingId === fila.id}
                      onChange={(e) => toggleMateria(fila, e.target.checked)}
                    />
                    <label className="form-check-label small" htmlFor={`dm-${fila.id}`}>
                      {fila.materia_nombre}
                    </label>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
}

// Variante para cuando el docente TODAVÍA no existe (modal crear): no hay
// docenteId para pegarle a la API, así que solo junta la selección en estado
// local — recién se manda al backend cuando el docente ya fue creado.
function MateriasSeleccionCrear({ seleccionadas, onChange }) {
  const [cursos, setCursos] = useState([]);
  const [cursoId, setCursoId] = useState("");
  const [mallaCurso, setMallaCurso] = useState(null);
  const [mallaLoading, setMallaLoading] = useState(false);

  useEffect(() => {
    listarCursos()
      .then((res) => setCursos(Array.isArray(res) ? res : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!cursoId) { setMallaCurso(null); return; }
    setMallaLoading(true);
    mallaService.obtenerMallaPorCurso(cursoId)
      .then((res) => setMallaCurso(Array.isArray(res?.materias) ? res.materias : (Array.isArray(res) ? res : [])))
      .catch((err) => showToast({ type: "danger", message: err.message || "No se pudo cargar la malla de ese curso." }))
      .finally(() => setMallaLoading(false));
  }, [cursoId]);

  const toggle = (cursosMateriasId, marcado) => {
    onChange(
      marcado
        ? [...seleccionadas, cursosMateriasId]
        : seleccionadas.filter((id) => id !== cursosMateriasId)
    );
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <p className="text-muted small mb-3">
          Opcional: marca de una vez qué materias dictará (por curso). Se le asignan apenas
          se cree el docente.
        </p>
        {seleccionadas.length > 0 && (
          <p className="small mb-3">
            <span className="badge bg-primary">{seleccionadas.length} materia{seleccionadas.length === 1 ? "" : "s"} seleccionada{seleccionadas.length === 1 ? "" : "s"}</span>
          </p>
        )}
        <select
          className="form-select form-select-sm mb-3"
          value={cursoId}
          onChange={(e) => setCursoId(e.target.value)}
        >
          <option value="">Selecciona un curso...</option>
          {cursos.map((c) => (
            <option key={c.id} value={c.id}>{c.nombre}</option>
          ))}
        </select>

        {mallaLoading && <p className="text-muted small mb-0">Cargando materias del curso...</p>}

        {!mallaLoading && mallaCurso && (
          mallaCurso.length === 0 ? (
            <p className="text-muted small mb-0">Este curso no tiene materias en su malla todavía.</p>
          ) : (
            <div className="d-flex flex-column gap-1" style={{ maxHeight: 220, overflowY: "auto" }}>
              {mallaCurso.map((fila) => (
                <div className="form-check" key={fila.id}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`dmc-${fila.id}`}
                    checked={seleccionadas.includes(fila.id)}
                    onChange={(e) => toggle(fila.id, e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor={`dmc-${fila.id}`}>
                    {fila.materia_nombre}
                  </label>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}

const ModalWrapper = ({ open, title, children, onClose, size = "modal-lg" }) => {
  if (!open) return null;
  return (
    <div
      className="modal fade show d-block management-modal"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
    >
      <div className={`modal-dialog ${size} modal-dialog-centered`} role="document">
        <div className="modal-content">
          <div className="modal-header border-0">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

export default function DocentesFormModal({
  modal = "",
  formNuevo = {},
  setFormNuevo = () => {},
  formEditar = {},
  setFormEditar = () => {},
  savingNuevo = false,
  savingEditar = false,
  deleting = false,
  docenteSeleccionado = null,
  cerrarModal = () => {},
  handleCrearDocente = () => {},
  handleActualizarDocente = () => {},
  handleEliminarDocente = () => {},
  canUpdate = true,
  canDelete = true,
}) {
  const formNuevoId = "docentes-form-nuevo";
  const formEditarId = "docentes-form-editar";
  const isNuevoValido =
    formNuevo.nombres?.trim() && formNuevo.apellidos?.trim() && formNuevo.documento?.trim();
  const isEditarValido =
    formEditar.nombres?.trim() && formEditar.apellidos?.trim() && formEditar.documento?.trim();
  const [seccionEditar, setSeccionEditar] = useState("datos"); // "datos" | "materias" — una a la vez
  useEffect(() => {
    if (modal === "editar") setSeccionEditar("datos");
  }, [modal, docenteSeleccionado]);
  // Si se borra un dato obligatorio estando en "materias", no lo dejamos ahi
  // varado sin poder guardar — lo regresamos a completar "datos" primero.
  useEffect(() => {
    if (seccionEditar === "materias" && !isEditarValido) setSeccionEditar("datos");
  }, [isEditarValido, seccionEditar]);

  const [seccionCrear, setSeccionCrear] = useState("datos");
  const [materiasCrear, setMateriasCrear] = useState([]);
  useEffect(() => {
    if (modal === "crear") {
      setSeccionCrear("datos");
      setMateriasCrear([]);
    }
  }, [modal]);
  // Primero se completan los datos obligatorios del docente — recien ahi se
  // habilita marcar materias (evita quedar atascado en esa pestaña sin poder
  // guardar porque el formulario de datos sigue invalido).
  useEffect(() => {
    if (seccionCrear === "materias" && !isNuevoValido) setSeccionCrear("datos");
  }, [isNuevoValido, seccionCrear]);

  return (
    <>
      <ModalWrapper open={modal === "crear"} title="Nuevo docente" onClose={cerrarModal} size="modal-lg">
        <div className="modal-body">
          <ul className="nav nav-tabs dashboard-tabs mb-3">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${seccionCrear === "datos" ? "active" : ""}`}
                onClick={() => setSeccionCrear("datos")}
              >
                Datos del docente
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${seccionCrear === "materias" ? "active" : ""}`}
                onClick={() => isNuevoValido && setSeccionCrear("materias")}
                disabled={!isNuevoValido}
                title={!isNuevoValido ? "Completa nombres, apellidos y documento primero" : undefined}
              >
                Materias que dictará
                {materiasCrear.length > 0 && (
                  <span className="badge bg-light text-dark ms-2">{materiasCrear.length}</span>
                )}
              </button>
            </li>
          </ul>

          {/* display:none en vez de desmontar — el boton "Guardar" del footer
              apunta a este <form> por id, tiene que seguir existiendo en el
              DOM sin importar que pestaña se este viendo. */}
          <div className="card border-0 shadow-sm" style={{ display: seccionCrear === "datos" ? "block" : "none" }}>
            <div className="card-body">
              <DocentesForm
                modo="crear"
                form={formNuevo}
                setForm={setFormNuevo}
                onSubmit={(e) => handleCrearDocente(e, materiasCrear)}
                loading={savingNuevo}
                formId={formNuevoId}
                showActions={false}
              />
            </div>
          </div>
          <div style={{ display: seccionCrear === "materias" ? "block" : "none" }}>
            <MateriasSeleccionCrear seleccionadas={materiasCrear} onChange={setMateriasCrear} />
          </div>
        </div>
        <div className="modal-footer border-0">
          {seccionCrear === "materias" && (
            <button
              type="button"
              className="btn btn-outline-secondary me-auto"
              onClick={() => setSeccionCrear("datos")}
              disabled={savingNuevo}
            >
              <i className="bi bi-arrow-left me-1" />
              Atrás
            </button>
          )}
          <button type="button" className="btn btn-outline-secondary" onClick={cerrarModal} disabled={savingNuevo}>
            Cancelar
          </button>
          {seccionCrear === "datos" ? (
            <button
              type="button"
              className="btn management-btn-primary"
              disabled={!isNuevoValido}
              onClick={() => setSeccionCrear("materias")}
            >
              Siguiente
              <i className="bi bi-arrow-right ms-1" />
            </button>
          ) : (
            <button
              type="submit"
              form={formNuevoId}
              className="btn management-btn-primary"
              disabled={savingNuevo || !isNuevoValido}
            >
              {savingNuevo ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Guardando...
                </>
              ) : (
                "Crear docente"
              )}
            </button>
          )}
        </div>
      </ModalWrapper>

      <ModalWrapper
        open={modal === "editar" && canUpdate}
        title="Editar docente"
        onClose={cerrarModal}
        size="modal-lg"
      >
        <div className="modal-body">
          <ul className="nav nav-tabs dashboard-tabs mb-3">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${seccionEditar === "datos" ? "active" : ""}`}
                onClick={() => setSeccionEditar("datos")}
              >
                Datos del docente
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${seccionEditar === "materias" ? "active" : ""}`}
                onClick={() => isEditarValido && setSeccionEditar("materias")}
                disabled={!isEditarValido}
                title={!isEditarValido ? "Completa nombres, apellidos y documento primero" : undefined}
              >
                Materias que dicta
              </button>
            </li>
          </ul>

          <div className="card border-0 shadow-sm" style={{ display: seccionEditar === "datos" ? "block" : "none" }}>
            <div className="card-body">
              <DocentesForm
                modo="editar"
                form={formEditar}
                setForm={setFormEditar}
                onSubmit={handleActualizarDocente}
                loading={savingEditar}
                formId={formEditarId}
                showActions={false}
              />
            </div>
          </div>
          <div style={{ display: seccionEditar === "materias" ? "block" : "none" }}>
            {docenteSeleccionado && <MateriasQueDicta docenteId={docenteSeleccionado.id} />}
          </div>
        </div>
        <div className="modal-footer border-0">
          <button type="button" className="btn btn-outline-secondary" onClick={cerrarModal} disabled={savingEditar}>
            Cancelar
          </button>
          <button
            type="submit"
            form={formEditarId}
            className="btn management-btn-primary"
            disabled={savingEditar || !isEditarValido || !canUpdate}
          >
            {savingEditar ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Guardando...
              </>
            ) : (
              "Guardar"
            )}
          </button>
        </div>
      </ModalWrapper>

      <ModalWrapper
        open={modal === "eliminar" && canDelete}
        title="Eliminar docente"
        onClose={cerrarModal}
        size="modal-md"
      >
        <div className="modal-body">
          <p className="mb-2">
            Estas a punto de eliminar al docente{" "}
            <strong>
              {docenteSeleccionado?.nombres} {docenteSeleccionado?.apellidos}
            </strong>
            .
          </p>
          <p className="text-muted mb-0">
            Esta accion no se puede deshacer. Si este docente tiene asignaciones activas, no podra ser eliminado.
          </p>
        </div>
        <div className="modal-footer border-0">
          <button type="button" className="btn btn-outline-secondary" onClick={cerrarModal} disabled={deleting}>
            Cancelar
          </button>
          <button type="button" className="btn btn-danger" onClick={handleEliminarDocente} disabled={deleting || !canDelete}>
            {deleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Eliminando...
              </>
            ) : (
              "Eliminar"
            )}
          </button>
        </div>
      </ModalWrapper>
    </>
  );
}
